import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"
import { Button, styled, Typography } from "@mui/material"
import type { ComponentType } from "react"
import { useRef } from "react"
import { type DropTargetMonitor, useDrop } from "react-dnd"
import { NativeTypes } from "react-dnd-html5-backend"

const DropZoneContainer = styled("label")<{ isOver: boolean; hasError: boolean }>(({ theme, isOver, hasError }) => ({
	position: "relative",
	border: `1px dashed ${hasError ? theme.palette.error.main : isOver ? theme.palette.primary.main : theme.palette.grey[700]}`,
	backgroundColor: isOver ? theme.palette.grey[100] : theme.palette.grey[50],
	borderRadius: theme.shape.borderRadius,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	height: "96px",
	cursor: "pointer",
	transition: "background-color 0.2s ease, border-color 0.2s ease",
	"&:hover": {
		backgroundColor: theme.palette.grey[200],
	},
}))

const HiddenInput = styled("input")({
	position: "absolute",
	inset: 0,
	opacity: 0,
	width: "100%",
	cursor: "pointer",
})

const ButtonContainer = styled("div")({
	pointerEvents: "none",
})

const FileInfo = styled(Typography)(({ theme }) => ({
	marginTop: theme.spacing(1),
	fontSize: "0.875rem",
	color: theme.palette.text.secondary,
}))

const ErrorMessage = styled(Typography)(({ theme }) => ({
	marginTop: theme.spacing(1),
	fontSize: "0.875rem",
	color: theme.palette.error.main,
}))

export interface FileDropZoneProps {
	onFileChange: (file: File | FileList | null) => void
	validationError?: string | null
	isImporting?: boolean
	file?: File | FileList | null
	accept?: string
	buttonText?: string
	icon?: ComponentType
	disabled?: boolean
	multiple?: boolean
	required?: boolean
}

/**
 * Requires an ancestor `DndProvider` with a compatible backend (e.g. `HTML5Backend` from `react-dnd-html5-backend`).
 */
export function FileDropZone({
	onFileChange,
	validationError = null,
	isImporting = false,
	file = null,
	accept = ".json,application/json",
	buttonText = "Select JSON file...",
	icon: Icon = InsertDriveFileIcon,
	disabled = false,
	multiple = false,
	required = false,
}: FileDropZoneProps) {
	const fileInputRef = useRef<HTMLInputElement>(null)
	const dropZoneRef = useRef<HTMLLabelElement>(null)

	const isDisabled = disabled || isImporting

	const [{ isOver }, dropRef] = useDrop({
		accept: NativeTypes.FILE,
		collect: (monitor: DropTargetMonitor) => ({
			isOver: monitor.isOver(),
		}),
		drop: (item: { files: File[] }) => {
			if (item.files && item.files.length > 0) {
				if (multiple) {
					// Create a FileList-like structure for multiple files
					const dataTransfer = new DataTransfer()
					for (const file of item.files) {
						dataTransfer.items.add(file)
					}
					onFileChange(dataTransfer.files)
				} else {
					onFileChange(item.files[0])
				}
			}
		},
		canDrop: () => !isDisabled,
	})

	dropRef(dropZoneRef)

	const handleClick = () => {
		if (!isDisabled) {
			fileInputRef.current?.click()
		}
	}

	const handleKeyDown = (event: React.KeyboardEvent<HTMLLabelElement>) => {
		if ((event.key === "Enter" || event.key === " ") && !isDisabled) {
			event.preventDefault()
			fileInputRef.current?.click()
		}
	}

	const handleInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
		// Prevent click event from bubbling to the container
		event.stopPropagation()
	}

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files
		if (files && files.length > 0) {
			if (multiple) {
				// Create a new FileList using DataTransfer to preserve the files
				// This is necessary because resetting the input invalidates the original FileList
				const dataTransfer = new DataTransfer()
				for (let i = 0; i < files.length; i++) {
					dataTransfer.items.add(files[i])
				}
				onFileChange(dataTransfer.files)
			} else {
				onFileChange(files[0])
			}
		} else {
			onFileChange(null)
		}
		// Reset input to allow re-uploading the same file
		// Note: We reset after creating a copy of the FileList to ensure it remains valid
		event.target.value = ""
	}

	const hasError = !!validationError

	const fileInputRefId = fileInputRef.current?.id || "file-dropzone-input"

	return (
		<>
			<DropZoneContainer
				ref={dropZoneRef}
				tabIndex={isDisabled ? -1 : 0}
				isOver={isOver}
				hasError={hasError}
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				htmlFor={fileInputRefId}
			>
				<ButtonContainer>
					<Button startIcon={<Icon />} disabled={isDisabled}>
						{buttonText}
					</Button>
				</ButtonContainer>
				<HiddenInput
					id={fileInputRefId}
					ref={fileInputRef}
					type="file"
					accept={accept}
					multiple={multiple}
					required={required}
					disabled={isDisabled}
					onClick={handleInputClick}
					onChange={handleFileChange}
				/>
			</DropZoneContainer>
			{file && !validationError && (
				<FileInfo>
					{multiple && file instanceof FileList
						? `Selected: ${file.length} file${file.length > 1 ? "s" : ""}`
						: file instanceof File
							? `Selected: ${file.name}`
							: null}
				</FileInfo>
			)}
			{validationError && <ErrorMessage>{validationError}</ErrorMessage>}
		</>
	)
}
